import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IDevice } from '@pages/Assets/Inventory/types';
import { Radio, Space } from 'antd';
import { BiBlock, BiCheckCircle } from 'react-icons/bi';
import { MdSignalCellular4Bar } from 'react-icons/md';
import { IDeviceActionRequestPayload } from '../types';
import useUpload from './Upload';

interface IUseImeiLockUnlockProps {
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  devicesProps: IDevice[] | undefined;
  uploadedDevicesState: any;
  currentStep: number;
}

function useImeiLockUnlock({
  devicesProps,
  handleDeviceActions,
  uploadedDevicesState,
  currentStep,
}: IUseImeiLockUnlockProps) {
  const { t: translate } = useTranslation(['device-actions-modal']);
  const [extraAction, setExtraAction] = useState(22);
  const [uploadedDevices, setUploadedDevices] = uploadedDevicesState;

  const isUpload = useMemo(
    () => !devicesProps || devicesProps.length === 0,
    [devicesProps],
  );

  const contentSteps = [
    {
      title: translate('steps.imei-lock-unlock.title'),
      content: (
        <Space
          direction="vertical"
          size="large"
          align="center"
          style={{ width: '100%' }}
        >
          <div className="network-block-radio">
            <Radio.Group
              name="radiogroup"
              defaultValue={22}
              style={{ display: 'flex', justifyContent: 'center' }}
              value={extraAction}
              onChange={e => {
                setExtraAction(e.target.value);
              }}
            >
              <Radio.Button value={22} className="block-card-option">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ position: 'relative', lineHeight: '1' }}>
                    <MdSignalCellular4Bar className="signal-icon" />
                    <BiBlock className="block-icon" />
                  </span>
                </div>
                <div style={{ lineHeight: '1' }}>
                  {translate('steps.network-block.label-button-block')}
                </div>
              </Radio.Button>
              <Radio.Button value={23} className="block-card-option">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ position: 'relative', lineHeight: '1' }}>
                    <MdSignalCellular4Bar className="signal-icon" />
                    <BiCheckCircle className="unblock-icon" />
                  </span>
                </div>
                <div style={{ lineHeight: '1' }}>
                  {translate('steps.network-block.label-button-unblock')}
                </div>
              </Radio.Button>
            </Radio.Group>
          </div>
        </Space>
      ),
    },
  ];

  const { uploadStep, uploadReinitState } = useUpload({
    actionKey: extraAction,
    setUploadedDevices,
  });

  const onSubmit = useCallback(async () => {
    const payload: IDeviceActionRequestPayload = {};
    payload.action = extraAction;

    payload.devices = isUpload
      ? uploadedDevices
      : devicesProps?.map(device => ({
          msisdn: device.msisdn,
        }));
    return handleDeviceActions(payload);
  }, [
    extraAction,
    isUpload,
    uploadedDevices,
    devicesProps,
    handleDeviceActions,
  ]);

  const nextStepIsDisabled = useCallback(() => {
    if (currentStep === 0) {
      return !extraAction;
    }
    if (isUpload) {
      return !uploadedDevices || uploadedDevices?.length === 0;
    }
    return false;
  }, [currentStep, extraAction, isUpload, uploadedDevices]);

  const imeiLockUnlockData = {
    contentSteps: isUpload ? [...contentSteps, uploadStep] : contentSteps,
    onSubmit,
    reinitState: () => uploadReinitState(),
    extraAction,
    nextStepIsDisabled,
  };

  return { imeiLockUnlockData };
}

export default useImeiLockUnlock;
